import { md5 } from "./md5.ts";

/**
 * ESP-IDF's binary partition table, as `gen_esp32part.py` writes it: 32-byte
 * entries from the start of a 4 KB flash sector, an optional MD5 entry over
 * the entries before it, and 0xFF bytes after the last entry.
 */

/** Where ESP-IDF puts the table unless CONFIG_PARTITION_TABLE_OFFSET says otherwise. */
export const DEFAULT_TABLE_OFFSET = 0x8000;
/** The table's entries fit in this many bytes; the rest of its sector is for a signature. */
export const TABLE_LENGTH = 0xc00;
export const SECTOR = 0x1000;

const ENTRY = 32;
const MAGIC = [0xaa, 0x50];
const MD5_MAGIC = [0xeb, 0xeb];

export interface Partition {
    label: string;
    type: number;
    subtype: number;
    offset: number;
    size: number;
    encrypted: boolean;
    readonly: boolean;
}

export interface PartitionTable {
    partitions: Partition[];
    /** Whether the table carries an MD5 entry, which matched. */
    checked: boolean;
}

/** What `parsePartitionTable` throws for bytes that are not a valid table. */
export class NotATable extends Error {}

/** Read the table at the start of `bytes`; throws `NotATable` with the reason when it is not one. */
export function parsePartitionTable(bytes: Uint8Array): PartitionTable {
    const partitions: Partition[] = [];
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    for (let at = 0; at + ENTRY <= Math.min(bytes.length, TABLE_LENGTH); at += ENTRY) {
        const entry = bytes.subarray(at, at + ENTRY);
        if (startsWith(entry, MD5_MAGIC)) {
            const expected = entry.subarray(16, 32);
            if (!equal(md5(bytes.subarray(0, at)), expected)) {
                throw new NotATable("the table's MD5 does not match its entries");
            }
            return valid({ partitions, checked: true });
        }
        if (entry.every((byte) => byte === 0xff)) return valid({ partitions, checked: false });
        if (!startsWith(entry, MAGIC)) {
            throw new NotATable(at === 0 ? "no partition table here" : `entry ${at / ENTRY} has no magic number`);
        }
        const flags = view.getUint32(at + 28, true);
        partitions.push({
            label: label(entry.subarray(12, 28)),
            type: entry[2]!,
            subtype: entry[3]!,
            offset: view.getUint32(at + 4, true),
            size: view.getUint32(at + 8, true),
            encrypted: (flags & 1) !== 0,
            readonly: (flags & 2) !== 0,
        });
    }
    throw new NotATable("the table has no end");
}

function valid(table: PartitionTable): PartitionTable {
    if (table.partitions.length === 0) throw new NotATable("the table is empty");
    return table;
}

/**
 * The first sector of `bytes`, read from flash at `base`, that holds a valid
 * table, as a flash offset. Finds a table whose offset the build does not say.
 */
export function findPartitionTable(
    bytes: Uint8Array,
    base: number,
): { offset: number; table: PartitionTable } | undefined {
    const first = Math.ceil(base / SECTOR) * SECTOR - base;
    for (let at = first; at + ENTRY <= bytes.length; at += SECTOR) {
        if (!startsWith(bytes.subarray(at), MAGIC)) continue;
        try {
            const table = parsePartitionTable(bytes.subarray(at, at + TABLE_LENGTH));
            // gen_esp32part.py aligns every partition to a sector; other bytes rarely are.
            if (table.partitions.every((p) => p.offset % SECTOR === 0 && p.size > 0))
                return { offset: base + at, table };
        } catch {
            // Not a table after all; a later sector may hold one.
        }
    }
    return undefined;
}

const TYPES = ["app", "data", "bootloader", "partition_table"];
const SUBTYPES: Record<number, Record<number, string>> = {
    0: { 0x00: "factory", 0x20: "test" },
    1: {
        0x00: "ota",
        0x01: "phy",
        0x02: "nvs",
        0x03: "coredump",
        0x04: "nvs_keys",
        0x05: "efuse",
        0x06: "undefined",
        0x80: "esphttpd",
        0x81: "fat",
        0x82: "spiffs",
        0x83: "littlefs",
        0x90: "tee_ota",
    },
    2: { 0x00: "primary", 0x01: "ota", 0x02: "recovery" },
    3: { 0x00: "primary", 0x01: "ota" },
};

/** The type's name in a partition CSV file, or its number for a custom type. */
export function typeName(type: number): string {
    return TYPES[type] ?? hex(type);
}

export function subtypeName(type: number, subtype: number): string {
    if (type === 0 && subtype >= 0x10 && subtype < 0x20) return `ota_${subtype - 0x10}`;
    if (type === 0 && subtype >= 0x30 && subtype < 0x32) return `tee_${subtype - 0x30}`;
    return SUBTYPES[type]?.[subtype] ?? hex(subtype);
}

export function hex(value: number): string {
    return `0x${value.toString(16)}`;
}

/** "4 MB", "24 KB", or bytes when it is not a whole number of KB. */
export function formatSize(bytes: number): string {
    if (bytes >= 0x100000 && bytes % 0x100000 === 0) return `${bytes / 0x100000} MB`;
    if (bytes % 0x400 === 0) return `${bytes / 0x400} KB`;
    return `${bytes} bytes`;
}

function label(bytes: Uint8Array): string {
    const end = bytes.indexOf(0);
    return new TextDecoder().decode(end < 0 ? bytes : bytes.subarray(0, end));
}

function startsWith(bytes: Uint8Array, prefix: number[]): boolean {
    return prefix.every((byte, i) => bytes[i] === byte);
}

function equal(a: Uint8Array, b: Uint8Array): boolean {
    return a.length === b.length && a.every((byte, i) => byte === b[i]);
}
